/**
 * The AUTHORING schema — what an operator edits, and what the UI binds to.
 *
 * This is deliberately NOT the QPC tariff JSON, and not the engine's PieceSet
 * either. Three layers, three jobs:
 *
 *   Programme (this file)  what a human writes and a form can render
 *          |  compile()
 *   PieceSet (types.ts)    what the search algorithm consumes
 *          |  materialise()
 *   rate cells (ARI)       what channels and the booking widget read
 *
 * ---------------------------------------------------------------------------
 * WHY NOT KEEP THE QPC NAMES
 * ---------------------------------------------------------------------------
 *
 * Every engine bug found during the 2020-2026 differential traced back to a
 * field whose name misdescribes it. That is not an aesthetic complaint; it is
 * the measured cause. Concretely:
 *
 *  - `min_booking_price` is not the price of a booking. It is a floor on the
 *    WHOLE booking total that this piece imposes if used. Reading it as a price
 *    is what produced a quote of EUR 351 against a EUR 365 minimum.
 *
 *  - `end_prices` is not a price. It is the asymptote a decaying rate converges
 *    to, and it does NOTHING when it sits on the unreachable side of the step
 *    price. Two tariffs identical except for that had completely different
 *    curves — one decayed, one stayed linear — with nothing in the JSON saying
 *    so. Here that becomes `taper.floorPerNight` plus a diagnostic.
 *
 *  - `period` had three spellings: `{start, end}` with end INCLUSIVE,
 *    `{arrival, departure}` with departure EXCLUSIVE, and `{start, length}`.
 *    One field, three meanings, off-by-one between them. Here every range is
 *    `from`/`until`, half-open, always.
 *
 *  - `start_after` counts expansion steps but reads like a date.
 *
 *  - `prices: [{id: 0, value: 35}]` keys surcharges by array position, so a
 *    booking request is `items: [2, 1]` and nothing says which is the dog.
 *
 *  - `continue_when_broken` is the one semantic nobody could infer, and it
 *    governs the long-stay tariffs carrying about half of park revenue.
 *
 * The rule applied throughout: A NAME STATES WHAT THE FIELD DOES TO THE PRICE.
 * Where that made a name longer, it got longer.
 */

import type {
  Day, DayRange, Extension, Money, Piece, PieceSet, PriceExpr, RoundingMode, WeekdayMask,
} from './types';
import { ALL_WEEKDAYS } from './types';

/** Re-exported: an authored programme names its rounding, so callers need it. */
export type { RoundingMode } from './types';

/** ISO calendar date, `YYYY-MM-DD`. */
export type IsoDate = string;

/**
 * A half-open date range: `from` inclusive, `until` exclusive. ALWAYS.
 *
 * For a stay this reads naturally as arrival/departure — a guest arriving the
 * 3rd and leaving the 10th occupies [03, 10) and pays 7 nights. The QPC format
 * also offered an inclusive spelling, and mixing the two is a standing
 * off-by-one generator. There is exactly one spelling here.
 */
export interface DateRange {
  from: IsoDate;
  until: IsoDate;
}

/** Weekday names, so a config never depends on whether the week starts Sunday. */
export type Weekday =
  | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export const WEEKDAY_ORDER: Weekday[] = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];

/**
 * Named guest quantities. The operator defines them once per programme, so a
 * price line says `perUnit: { dog: 4 }` instead of `{id: 1, value: 4}`.
 */
/**
 * How a distribution channel counts a guest.
 *
 * Deliberately a CLOSED set, and deliberately short. A park may invent as many
 * guest categories as its price list needs — babies, kleuters, kinderen,
 * jeugd, volwassenen — but every OTA has one fixed, unnegotiable rule for how
 * a party is counted, and an availability feed has to be expressed in that
 * rule's terms. So each park category declares which of these it maps onto.
 *
 * The mapping is many-to-one on purpose: three age bands can all map to
 * `child`, and the feed simply reports their sum. Going the other way — a park
 * category that maps to nothing a channel understands — is what `other` is
 * for: dogs, cars, extra tents. They are counted, and charged, and they are
 * never people.
 */
export type ChannelRole = 'adult' | 'child' | 'infant' | 'other';

export interface QuantityDef {
  /** Stable key used in price expressions and in a booking request. */
  key: string;
  label: string;
  /** Units included in the base price before the surcharge applies. */
  includedInBase?: number;
  /**
   * Age band, `[ageFrom, ageUntil)` in years.
   *
   * Only meaningful for people, and only for parks that price by age. A park
   * where "children over three pay the adult rate" says that by declaring one
   * category, not by declaring two and giving them the same price.
   */
  ageFrom?: number;
  ageUntil?: number;
  /** How a channel counts this category. Defaults by `countsTowardOccupancy`. */
  channel?: ChannelRole;
  /**
   * Other roles this category BILLS — not other things it reports as.
   *
   * A park that does not price children separately says so by declaring one
   * category, not two with the same amount in them. That is already this
   * model's position (see `ageFrom`), and it works right up to the moment a
   * party has to be expressed in the one vocabulary every channel counts in:
   * a guest picker offers adults and children because those are the words a
   * visitor knows, and a card with only an adult category can price neither
   * the child nor, therefore, the family.
   *
   * REPORTED, from a live campsite: a family with a child got "ask us for a
   * price" on a pitch that takes children perfectly happily. The park's own
   * form had always asked for "adults" and "children from 3" and added them
   * together into one slot.
   *
   * So the category accepts the roles listed here as well as its own, and they
   * are counted TOGETHER — which is the whole point. `includedInBase: 2` on a
   * category billing adults and children means two guests between them: two
   * adults, or an adult and a child, and neither pays for a third place.
   *
   * `channel` is unchanged and still the direction OUT: it is what this
   * category reports as to an OTA. A park that cannot tell a child from an
   * adult in its prices cannot tell one in its feed either, and saying so once
   * here is better than a second field that can disagree with this one.
   */
  alsoCounts?: ChannelRole[];
  /**
   * Does one of these consume one of the included places?
   *
   * This is the field that differs between parks with the same price list. A
   * baby that sleeps in a travel cot may not count against "two people
   * included" at one park and may count at the next. Defaults to true for
   * anything not mapped to `other`, which is the common case.
   */
  countsTowardOccupancy?: boolean;
}

/** Whether a declared quantity is a person for occupancy purposes. */
export function countsAsPerson(q: QuantityDef): boolean {
  if (q.countsTowardOccupancy !== undefined) return q.countsTowardOccupancy;
  return channelRoleOf(q) !== 'other';
}

/**
 * Every role a category bills: the one it reports as, and any it absorbs.
 *
 * Ordered, and the first is `channelRoleOf` — callers that need one answer
 * (a feed, a label) take the head, and callers matching a party take the set.
 */
export function billedRolesOf(q: QuantityDef): ChannelRole[] {
  const own = channelRoleOf(q);
  const extra = (q.alsoCounts ?? []).filter((r) => r !== own);
  return [own, ...extra];
}

/** A category's channel role, defaulted for programmes that predate the field. */
export function channelRoleOf(q: QuantityDef): ChannelRole {
  if (q.channel) return q.channel;
  if (q.countsTowardOccupancy === false) return 'other';
  return 'adult';
}

/** A money amount built from a flat base plus named per-unit surcharges. */
export interface Price {
  /** Flat amount for the whole block. */
  base?: Money;
  /** Surcharge per unit of a named quantity, e.g. `{ adult: 8.5, dog: 4 }`. */
  perUnit?: Record<string, Money>;
  /** Multiply the per-unit surcharges by the nights covered. */
  perUnitIsPerNight?: boolean;
}

/**
 * How a repeated block's price changes as the stay lengthens.
 *
 * QPC spread this across six interacting scalars (`price_factor`,
 * `start_after`, `start_price_factor`, `if_factor_is_total`,
 * `if_factor_includes_start`, `end_prices`). Whether they did anything at all
 * depended on a relationship between two of them that no name mentioned. A
 * discriminated union makes the shapes nameable in a UI and unrepresentable
 * when meaningless.
 */
export type Taper =
  /** Every extra night costs the same. The common case; make it the default. */
  | { mode: 'none' }
  /**
   * Each night past `afterNights` is multiplied by `factor` again, compounding
   * per night. `floorPerNight` is the value it converges to.
   */
  | {
    mode: 'perNight';
    afterNights: number;
    factor: number;
    floorPerNight?: Money;
  }
  /**
   * The WHOLE run's total is multiplied by `factor` once per night past
   * `afterNights`. Cheaper per night the longer the stay, applied to the total
   * rather than to the tail — QPC's `is_total_price_factor`.
   */
  | {
    mode: 'wholeStay';
    afterNights: number;
    factor: number;
    floorPerNight?: Money;
  };

/** A repeatable tail attached to a block. */
export interface Extend {
  /** Nights added per step. */
  stepNights: number;
  /** Price of one step, before any taper. */
  stepPrice: Price;
  minSteps?: number;
  maxSteps?: number;
  taper?: Taper;
  /**
   * When another rate interrupts this run, resume the taper where it stopped
   * instead of restarting at full price.
   *
   * QPC called this `continue_when_broken`. It is the difference between
   * EUR 1655 and EUR 1681 on a 21-night stay split by an arrangement, and it
   * sits on the long-stay low-season rates.
   */
  resumeAfterGap?: boolean;
}

/**
 * Availability effect of a rate, replacing QPC's overloaded `error` field —
 * which meant different things depending on the tariff's `type`, and was the
 * kind of overload that makes a UI impossible to write honestly.
 *
 * `refuses` and `closed` are genuinely different and the difference matters:
 * a `closed` rate covers its nights at ZERO cost, so it always wins any tiling
 * it can join, which is exactly how it blocks. A `refuses` rate is priced
 * normally and only blocks if the cheapest tiling happens to use it.
 */
export type Availability =
  /** Normal: bookable online at the computed price. */
  | 'open'
  /** Priced normally, but a booking that uses it cannot be taken. */
  | 'refuses'
  /** Covers its nights at zero cost and blocks the booking — QPC `errorTariff`. */
  | 'closed';

/**
 * What a rate is FOR.
 *
 * ---------------------------------------------------------------------------
 * MOSTLY EDITORIAL, AND `nightly` IS NOT
 * ---------------------------------------------------------------------------
 *
 * This said "purely a UI affordance — `compileProgramme` ignores it, and two
 * rates with the same numbers price identically whatever their role". That is
 * false, and believing it led to a change that repriced 26 of 28 ported cards
 * before a before-and-after check caught it.
 *
 * `ladder`, `arrangement` and `other` are editorial: they group a list and tell
 * the generator which rates it owns. **`nightly` is not.** It opts a rate into
 * `flattenNightly`, where overlapping bands are resolved so the narrowest window
 * wins and the others are cut away — so calling a rate nightly can change what a
 * stay costs, and calling a LIFTED rate nightly changes it in the direction that
 * breaks parity with the legacy engine, because that format layers competing day
 * tariffs on purpose.
 *
 * A screen that needs to know what an unroled rate is should ask `displayRole`
 * in `roles.ts`, which infers for the reader and never writes anything down.
 */
export type RateRole =
  /** One night, the base layer: what a night costs before any length discount. */
  | 'nightly'
  /** A length-of-stay step: seven nights priced below seven single nights. */
  | 'ladder'
  /** A named package over fixed dates — Easter, Ascension. */
  | 'arrangement'
  /** Anything else an operator writes by hand. */
  | 'other';

/**
 * Where a rate came from, so the generator can tell its own work from a human's.
 *
 * The rule this exists to enforce: **a rate a person has edited is never
 * silently overwritten.** The generator may propose a change to it, but the
 * proposal is shown with the hand-edit flagged and has to be accepted.
 */
export interface RateOrigin {
  /** Identifier of the generator run that produced it, e.g. `los-ladder@1`. */
  generator: string;
  /** Human-readable note on what it was derived from. */
  from?: string;
  /**
   * Id of the nightly rate this price was computed from.
   *
   * Not the same as "the band covering `active.from`": a discount is placeable
   * over a window wider than the band it was priced from, so the window start
   * often falls in a different, cheaper band. Inferring the basis from the
   * window is how the leakage check first produced a false positive on every
   * widened discount.
   */
  basis?: string;
  /** Set once an operator changes a generated rate. */
  editedByHand?: boolean;
  /**
   * What the GENERATOR had produced when a human first overruled it.
   *
   * An override is relative even when it looks absolute. Somebody sets a week to
   * €275 against a generated €300 — nine per cent off, deliberately. The nightly
   * band later rises and the generator would now produce €345; their €275 is
   * suddenly twenty per cent off, and nothing says the ground moved. The panel
   * shows a protected rung and a proposal, both correct, and no way to tell
   * whether the decision still means what it meant.
   *
   * Written once, by the store, at the moment of the first edit. It cannot be
   * recovered afterwards: by then the only stored price is the operator's.
   */
  generatedPrice?: Price;
}

/**
 * One authored rate. Replaces QPC's seven `type` values, which were four
 * spellings of "a block of N nights" plus three of "a block with a tail".
 */
export interface Rate {
  id: string;
  label: string;
  /** Free-text note shown in the dashboard, never used in pricing. */
  note?: string;
  /** Editorial grouping. Not used in pricing. */
  role?: RateRole;
  /** Provenance, when a generator produced this rate. Not used in pricing. */
  origin?: RateOrigin;
  /**
   * The library period `active` was copied from, if any.
   *
   * A COPY, deliberately, not a live reference: a published version must not
   * change because someone edited the holiday library. The id lets the UI
   * notice the library has moved and offer an update the operator accepts.
   */
  derivedFrom?: string;

  /** Nights this rate may cover, half-open. */
  active: DateRange;
  /**
   * Optional sub-windows inside `active`. A placement must fit wholly within
   * one of them. QPC: `allowed_periods`.
   */
  placementWindows?: DateRange[];

  /** Nights covered by one placement. */
  nights: number;
  /** A placement may be trimmed this short and still costs the full price. */
  minNights?: number;

  price: Price;
  extend?: Extend;

  /**
   * Which weekday a PLACEMENT of this rate may start / end on.
   *
   * Per placement, not per stay. On a one-night rate that means "only these
   * nights are priced here", which is almost never what an operator means by a
   * changeover day — for that, see `stayArriveOn`.
   */
  arriveOn?: Weekday[];
  departOn?: Weekday[];

  /**
   * Changeover days for the WHOLE STAY.
   *
   * "In summer you arrive on Saturday" is a fact about the booking, not about
   * each night of it, so it cannot be said with `arriveOn` on a nightly rate —
   * that would only mean Saturday nights are priced by this rate. These are
   * whole-stay eligibility: a stay arriving on the wrong day cannot use the
   * rate at all, so the nights it covers become unpriceable and the stay is
   * refused. That is the behaviour a channel calls closed-to-arrival.
   */
  stayArriveOn?: Weekday[];
  stayDepartOn?: Weekday[];

  /**
   * Using this rate makes the WHOLE booking cost at least this much.
   *
   * Not a price, and not a minimum for this rate alone — a floor on the final
   * total. Named at length on purpose: read as a price it produces quotes below
   * their own minimum, which is exactly the bug it caused.
   */
  requiresBookingTotalAtLeast?: Price;

  /** Whole-stay eligibility, checked once before any search. */
  stayNights?: { min?: number; max?: number };
  stayWithin?: DateRange;

  /** At most this many placements of this rate in one booking. */
  maxPlacements?: number;

  availability?: Availability;
  /**
   * The quote is valid but a human must confirm it before the booking is
   * taken. QPC `require_contact`.
   *
   * Orthogonal to `availability` on purpose: in QPC these were two independent
   * fields and a rate could set either, both, or neither. Folding them into one
   * union looked tidier and silently changed behaviour for the 21 rates that
   * ask for contact WITHOUT blocking.
   */
  needsStaffConfirmation?: boolean;
  message?: string;
}

/**
 * A multiplier applied to the accommodation subtotal after tiling.
 *
 * QPC's `factorTariff`, whose `min_arrival_date` guard was read from the wrong
 * object and therefore never fired — the ×1.2 "after 1 July" surcharge hit
 * bookings arriving in June for a whole season. Here the guards are named
 * fields on the surcharge itself, with nowhere else they could live.
 */
export interface Surcharge {
  id: string;
  label: string;
  multiplier: number;
  /** Applies when the stay overlaps this range. */
  whenStayOverlaps: DateRange;
  /** ...and only if arrival is on/after this date. */
  onlyIfArrivalFrom?: IsoDate;
  /** ...and only if departure is on/before this date. */
  onlyIfDepartureUntil?: IsoDate;
  rounding?: RoundingMode;
  message?: string;
}

/**
 * How many people (and animals) an accommodation takes, and how many the base
 * price covers.
 *
 * Two numbers do most of the work, and they are the two every channel already
 * speaks — Booking, Airbnb and every channel manager call them base occupancy
 * and maximum occupancy:
 *
 *   campsite pitch   included 2, max 6    — extra people are charged
 *   rental unit      included 4, max 4    — nobody extra is possible, so nothing to charge
 *   trekkershut      included 4, max 5    — the fifth person is charged
 *
 * The rental case is the one worth noticing: `included === max` expresses "the
 * price is the price, and five people do not fit" without any special casing.
 */
export interface Occupancy {
  /** People the base price covers. */
  included: number;
  /** Hard limit. A booking above it cannot be priced at all. */
  max: number;
  /**
   * Which declared quantities count as people.
   *
   * Defaults to every quantity except the pet one, which is what lets "children
   * over three count as adults" be said by simply not declaring a separate
   * child quantity — and lets a park that does distinguish them declare both
   * and have both count.
   */
  counts?: string[];
  /** Animals, counted and capped separately from people. */
  pets?: { quantity: string; max: number };
}

/**
 * What a fee multiplies.
 *
 * The four bases every channel and every Dutch tourist tax already use. Naming
 * the basis rather than hard-coding a fee type is what keeps this open: a
 * cleaning charge, a dog, a set of bed linen and the toeristenbelasting are the
 * same object with different bases.
 */
export type FeeBasis =
  /** Once for the booking: cleaning, a booking fee. */
  | 'perStay'
  /** Once per night, regardless of party size: a dog, a fridge. */
  | 'perNight'
  /** Once per person: bed linen, a welcome pack. */
  | 'perPerson'
  /** Per person per night: the tourist tax, and an extra person on a pitch. */
  | 'perPersonPerNight';

export interface Fee {
  id: string;
  label: string;
  basis: FeeBasis;
  /** Amount per unit of the basis. */
  amount: Money;
  /**
   * Which declared quantity this counts. Defaults to the occupancy counts for
   * the per-person bases, and is required for anything else (a dog fee counts
   * dogs).
   */
  quantity?: string;
  /**
   * Charge only the people beyond `occupancy.included`.
   *
   * This is what makes "two people are included, the third costs extra" one
   * fee rather than a rule buried in the rate card.
   */
  beyondIncluded?: boolean;
  /** The guest chooses it: towels, bed linen. Quoted but not assumed. */
  optional?: boolean;
  /**
   * Restrict to a date window, so a per-night extra can vary by season without
   * needing to live inside the rate card.
   */
  active?: DateRange;
  /** Editorial grouping; a tax is shown apart from a service. */
  kind?: 'fee' | 'tax' | 'extra';
  note?: string;
}

/** A complete, publishable tariff programme for one accommodation type. */
export interface Programme {
  schemaVersion: 1;
  id: string;
  label: string;
  /** Season this programme prices; informational, not a constraint. */
  season?: string;
  quantities: QuantityDef[];
  rates: Rate[];
  surcharges?: Surcharge[];
  rounding?: RoundingMode;
  /** Who fits, and who the base price covers. */
  occupancy?: Occupancy;
  /**
   * Charges on top of the accommodation price.
   *
   * Deliberately NOT part of the tiling search. The search finds the cheapest
   * way to cover the nights; a tourist tax is not an alternative way to cover a
   * night, and letting it into the search would mean the engine could "choose"
   * not to charge it. They are applied to the winning tiling instead, which
   * also keeps every existing guarantee about the search intact.
   */
  fees?: Fee[];
  /**
   * Arrival the dashboard opens on. Computed when a programme is imported, so
   * the first thing an operator sees is a real price rather than "not
   * available" on a date outside the season.
   */
  defaultArrival?: IsoDate;
}

// ---------------------------------------------------------------------------
// Diagnostics
// ---------------------------------------------------------------------------

export type Severity = 'error' | 'warning' | 'info';

export interface Diagnostic {
  severity: Severity;
  /** Dotted path to the offending field, for the UI to focus. */
  path: string;
  code: string;
  message: string;
}

const MS_PER_DAY = 86_400_000;

export function parseIso(value: IsoDate, path: string, out: Diagnostic[]): Day {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? '').trim());
  if (!m) {
    out.push({
      severity: 'error', path, code: 'bad-date',
      message: `"${value}" is not a YYYY-MM-DD date`,
    });
    return 0;
  }
  return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / MS_PER_DAY);
}

export function formatIso(day: Day): IsoDate {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10);
}

function range(r: DateRange, path: string, out: Diagnostic[]): DayRange {
  const start = parseIso(r.from, `${path}.from`, out);
  const end = parseIso(r.until, `${path}.until`, out);
  if (end <= start) {
    out.push({
      severity: 'error', path, code: 'empty-range',
      message: `${r.from} → ${r.until} covers no nights (ranges are half-open: `
        + 'from is included, until is not)',
    });
  }
  return { start, end };
}

function weekdayMask(days: Weekday[] | undefined, path: string, out: Diagnostic[]): WeekdayMask {
  if (!days || days.length === 0) return ALL_WEEKDAYS;
  let mask = 0;
  for (const d of days) {
    const i = WEEKDAY_ORDER.indexOf(d);
    if (i < 0) {
      out.push({
        severity: 'error', path, code: 'bad-weekday', message: `unknown weekday "${d}"`,
      });
      continue;
    }
    mask |= 1 << i;
  }
  return mask || ALL_WEEKDAYS;
}

function toPriceExpr(p: Price | undefined, quantities: QuantityDef[],
  path: string, out: Diagnostic[]): PriceExpr {
  const expr: PriceExpr = {};
  if (!p) return expr;
  if (p.base !== undefined) expr.base = p.base;
  if (p.perUnit) {
    const known = new Set(quantities.map((q) => q.key));
    const per = [];
    for (const [key, amount] of Object.entries(p.perUnit)) {
      if (!known.has(key)) {
        out.push({
          severity: 'error', path: `${path}.perUnit.${key}`, code: 'unknown-quantity',
          message: `"${key}" is not declared in quantities`,
        });
        continue;
      }
      const def = quantities.find((q) => q.key === key)!;
      per.push({
        quantity: key,
        amount,
        freeUnits: def.includedInBase,
        perNight: p.perUnitIsPerNight,
      });
    }
    if (per.length) expr.per = per;
  }
  return expr;
}

/**
 * Turn an authored taper into the engine's decay shape — and REPORT WHEN IT
 * WILL DO NOTHING.
 *
 * A taper whose floor sits on the unreachable side of the step price is inert:
 * the engine correctly charges the flat linear price, but the operator wrote
 * numbers that look like a discount and got none. In QPC this was invisible;
 * two structurally identical rates behaved completely differently and nothing
 * said why. Surfacing it is the single most valuable thing this layer does.
 */
function toDecay(
  taper: Taper | undefined, stepPrice: Money, path: string, out: Diagnostic[],
): Extension['decay'] {
  if (!taper || taper.mode === 'none') return undefined;

  if (taper.factor <= 0) {
    out.push({
      severity: 'error', path: `${path}.factor`, code: 'bad-factor',
      message: 'factor must be greater than 0 (1 means no change)',
    });
  }
  if (taper.afterNights < 0) {
    out.push({
      severity: 'error', path: `${path}.afterNights`, code: 'bad-afterNights',
      message: 'afterNights cannot be negative',
    });
  }
  if (taper.factor === 1) {
    out.push({
      severity: 'warning', path: `${path}.factor`, code: 'taper-noop',
      message: 'factor is 1, so this taper changes nothing. Use mode "none" to say that plainly.',
    });
  }
  if (taper.floorPerNight !== undefined && stepPrice > 0) {
    const decaying = taper.factor < 1;
    const unreachable = decaying
      ? taper.floorPerNight >= stepPrice
      : taper.floorPerNight <= stepPrice;
    if (unreachable) {
      out.push({
        severity: 'warning',
        path: `${path}.floorPerNight`,
        code: 'taper-inert',
        message: `floorPerNight ${taper.floorPerNight} is ${decaying ? 'at or above' : 'at or below'} `
          + `the step price ${stepPrice}, so a factor of ${taper.factor} can never reach it. `
          + 'The whole taper is ignored and every night costs the step price. '
          + (decaying
            ? 'For a discount, set floorPerNight BELOW the step price.'
            : 'For a surcharge, set floorPerNight ABOVE the step price.'),
      });
    }
  }

  return {
    factor: taper.factor,
    startAfter: taper.afterNights,
    startFactor: 1,
    endPrice: taper.floorPerNight !== undefined ? { base: taper.floorPerNight } : undefined,
    appliesToTotal: taper.mode === 'wholeStay',
    includesStart: taper.mode === 'wholeStay',
  };
}

export interface CompileResult {
  set: PieceSet;
  diagnostics: Diagnostic[];
  /** True when no diagnostic has severity 'error'. */
  ok: boolean;
}

/**
 * Compile an authored programme into the engine's PieceSet.
 *
 * Pure and total: it always returns a PieceSet plus diagnostics, so a dashboard
 * can price a half-finished programme and show the problems inline rather than
 * refusing to render.
 */
export function compileProgramme(p: Programme): CompileResult {
  const diagnostics: Diagnostic[] = [];
  const pieces: Piece[] = [];

  const seen = new Set<string>();
  for (const [i, q] of (p.quantities ?? []).entries()) {
    if (seen.has(q.key)) {
      diagnostics.push({
        severity: 'error', path: `quantities[${i}].key`, code: 'duplicate-quantity',
        message: `quantity key "${q.key}" is declared twice`,
      });
    }
    seen.add(q.key);
  }

  // Layers are resolved BEFORE pieces are built, so the engine only ever sees
  // non-overlapping nightly bands and never has to arbitrate between them.
  const authored = p.rates ?? [];
  const effective = flattenNightly(authored);

  const rateIds = new Set<string>();
  effective.forEach((rate, i) => {
    const path = `rates[${i}]`;
    if (rateIds.has(rate.id)) {
      diagnostics.push({
        severity: 'error', path: `${path}.id`, code: 'duplicate-rate',
        message: `rate id "${rate.id}" is used more than once`,
      });
    }
    rateIds.add(rate.id);

    const active = range(rate.active, `${path}.active`, diagnostics);
    if (rate.nights < 1) {
      diagnostics.push({
        severity: 'error', path: `${path}.nights`, code: 'bad-nights',
        message: 'a rate must cover at least one night',
      });
    }

    const windows = rate.placementWindows?.map(
      (w, j) => range(w, `${path}.placementWindows[${j}]`, diagnostics),
    );
    for (const [j, w] of (windows ?? []).entries()) {
      if (w.start < active.start || w.end > active.end) {
        diagnostics.push({
          severity: 'warning', path: `${path}.placementWindows[${j}]`, code: 'window-outside-active',
          message: 'this window falls partly outside the rate\'s active range, so the part '
            + 'outside can never be used',
        });
      }
    }

    const price = toPriceExpr(rate.price, p.quantities ?? [], `${path}.price`, diagnostics);
    const stepMoney = rate.extend?.stepPrice?.base ?? 0;

    const envelope = {
      id: rate.id,
      label: rate.label,
      validFrom: active.start,
      validTo: active.end,
      windows,
      arrivalWeekdays: rate.arriveOn ? weekdayMask(rate.arriveOn, `${path}.arriveOn`, diagnostics) : undefined,
      departureWeekdays: rate.departOn ? weekdayMask(rate.departOn, `${path}.departOn`, diagnostics) : undefined,
      stayArrivalWeekdays: rate.stayArriveOn
        ? weekdayMask(rate.stayArriveOn, `${path}.stayArriveOn`, diagnostics) : undefined,
      stayDepartureWeekdays: rate.stayDepartOn
        ? weekdayMask(rate.stayDepartOn, `${path}.stayDepartOn`, diagnostics) : undefined,
      price,
      minStayTotal: rate.requiresBookingTotalAtLeast
        ? toPriceExpr(rate.requiresBookingTotalAtLeast, p.quantities ?? [],
          `${path}.requiresBookingTotalAtLeast`, diagnostics)
        : undefined,
      minStayNights: rate.stayNights?.min,
      maxStayNights: rate.stayNights?.max,
      stayWindow: rate.stayWithin
        ? range(rate.stayWithin, `${path}.stayWithin`, diagnostics) : undefined,
      maxPlacements: rate.maxPlacements,
      message: rate.message,
      onRequest: rate.needsStaffConfirmation,
      refuses: rate.availability === 'refuses',
    };

    if (rate.availability === 'closed') {
      pieces.push({ ...envelope, price: {}, kind: 'closure', params: {} as Record<string, never> });
      return;
    }

    if (rate.extend) {
      const e = rate.extend;
      if (e.stepNights < 1) {
        diagnostics.push({
          severity: 'error', path: `${path}.extend.stepNights`, code: 'bad-step',
          message: 'an extension step must add at least one night',
        });
      }
      const extension: Extension = {
        nights: e.stepNights,
        price: toPriceExpr(e.stepPrice, p.quantities ?? [], `${path}.extend.stepPrice`, diagnostics),
        minSteps: e.minSteps,
        maxSteps: e.maxSteps,
        decay: toDecay(e.taper, stepMoney, `${path}.extend.taper`, diagnostics),
        continueWhenBroken: e.resumeAfterGap,
      };
      pieces.push({
        ...envelope,
        kind: 'extendable',
        params: { nights: rate.nights, minNights: rate.minNights ?? 1, extension },
      });
      return;
    }

    pieces.push({
      ...envelope,
      kind: 'simple',
      params: { nights: rate.nights, minNights: rate.minNights ?? 1 },
    });
  });

  const modifiers = (p.surcharges ?? []).map((s, i) => {
    const path = `surcharges[${i}]`;
    if (s.multiplier <= 0) {
      diagnostics.push({
        severity: 'error', path: `${path}.multiplier`, code: 'bad-multiplier',
        message: 'multiplier must be greater than 0 (1 means no change)',
      });
    }
    const window = range(s.whenStayOverlaps, `${path}.whenStayOverlaps`, diagnostics);
    const minArrival = s.onlyIfArrivalFrom
      ? parseIso(s.onlyIfArrivalFrom, `${path}.onlyIfArrivalFrom`, diagnostics) : undefined;
    const maxDeparture = s.onlyIfDepartureUntil
      ? parseIso(s.onlyIfDepartureUntil, `${path}.onlyIfDepartureUntil`, diagnostics) : undefined;
    if (minArrival !== undefined && minArrival >= window.end) {
      diagnostics.push({
        severity: 'warning', path: `${path}.onlyIfArrivalFrom`, code: 'guard-excludes-all',
        message: 'arrival guard starts after the overlap window ends, so this surcharge can '
          + 'never apply',
      });
    }
    return {
      id: s.id,
      label: s.label,
      factor: s.multiplier,
      window,
      minArrival,
      maxDeparture,
      message: s.message,
      rounding: s.rounding,
    };
  });

  //
  // A multiplier means the card does not say what a guest pays.
  //
  // The park's ported cards carry `price-increase#23`, x1.10, labelled "10%
  // duurder sinds 2025" — the VAT change of January 2026, applied as a
  // post-pass rather than written into the prices. It works, and it is
  // invisible: an operator reading 385 on a rate is looking at a stay that
  // costs 425, and the only place the multiplier appears is the total.
  //
  // It reached the 2027 drafts too, with its window widened to cover the whole
  // season, so every number typed for next year is charged ten per cent above
  // what was typed. The operator reported this as the factors "not working",
  // which is exactly how an invisible multiplier looks from the authoring side.
  //
  // Not an error. A multiplier is a legitimate way to express a change that
  // applies to everything, and deleting it would drop every rental price by a
  // tenth. But it must not be silent.
  for (const sur of (p.surcharges ?? [])) {
    if (sur.multiplier === 1) continue;
    const pct = Math.round((sur.multiplier - 1) * 1000) / 10;
    diagnostics.push({
      severity: 'warning',
      path: 'surcharges',
      code: 'multiplier-surcharge',
      message: `"${sur.label}" multiplies every price on this card by ${sur.multiplier} `
        + `(${pct > 0 ? '+' : ''}${pct}%) for stays between ${sur.whenStayOverlaps.from} `
        + `and ${sur.whenStayOverlaps.until}. The prices below are NOT what a guest pays: `
        + 'a rate of 100 is charged as ' + Math.round(100 * sur.multiplier) + '. '
        + 'Write the increase into the prices themselves and remove this, '
        + 'unless it is deliberately temporary.',
    });
  }

  // Layering is normal, so this reports what happened rather than complaining.
  // Without the resolution above it WOULD be a fault — the engine takes the
  // cheapest tiling, so the broad layer would win and the precise one would
  // silently never apply.
  //
  // ONE notice per band underneath, not one per pair. An operator pricing a
  // fortnight night by night produces a rate per stretch, each of which sits on
  // the same season band — and the panel showed forty-five separate notices
  // saying the same thing about the same band. A notice repeated forty-five
  // times is not information, it is a wall to be dismissed, and the next one
  // that matters is dismissed with it.
  const layeredOn = new Map<string, { on: string[]; from: string; until: string }>();
  for (const o of nightlyOverlaps(authored)) {
    const seen = layeredOn.get(o.a);
    if (!seen) { layeredOn.set(o.a, { on: [o.b], from: o.from, until: o.until }); continue; }
    seen.on.push(o.b);
    if (o.from < seen.from) seen.from = o.from;
    if (o.until > seen.until) seen.until = o.until;
  }

  for (const [under, o] of layeredOn) {
    const i = authored.findIndex((r) => r.id === o.on[0]);
    diagnostics.push({
      severity: 'info',
      path: `rates[${i < 0 ? 0 : i}].active`,
      code: 'nightly-layered',
      message: o.on.length === 1
        ? `"${o.on[0]}" sits on top of "${under}" from ${o.from} to ${o.until}. `
          + 'The narrower window wins over those nights, whether it is dearer or cheaper.'
        : `${o.on.length} rates sit on top of "${under}" between ${o.from} and ${o.until}. `
          + 'The narrower window wins over those nights, whether it is dearer or cheaper.',
    });
  }

  // ---------------------------------------------------------------------
  // Guest categories: the park's vocabulary, and its mapping to a channel's.
  // ---------------------------------------------------------------------
  const quantities = p.quantities ?? [];
  const guests: Record<string, { channel: ChannelRole; ageFrom?: number; ageUntil?: number }> = {};
  for (const q of quantities) {
    guests[q.key] = {
      channel: channelRoleOf(q),
      ageFrom: q.ageFrom,
      ageUntil: q.ageUntil,
    };
  }

  // Two categories claiming the same age is not a preference, it is a booking
  // that cannot be priced: a five-year-old would match both and the answer
  // would depend on which the operator happened to list first.
  const aged = quantities.filter(
    (q) => channelRoleOf(q) !== 'other' && (q.ageFrom !== undefined || q.ageUntil !== undefined),
  );
  for (let i = 0; i < aged.length; i++) {
    for (let j = i + 1; j < aged.length; j++) {
      const a = aged[i]; const b = aged[j];
      const aFrom = a.ageFrom ?? 0; const aUntil = a.ageUntil ?? Infinity;
      const bFrom = b.ageFrom ?? 0; const bUntil = b.ageUntil ?? Infinity;
      if (aFrom < bUntil && bFrom < aUntil) {
        diagnostics.push({
          severity: 'error', path: `quantities.${b.key}`, code: 'age-overlap',
          message: `"${a.label}" and "${b.label}" both cover age `
            + `${Math.max(aFrom, bFrom)}, so a guest that age matches two categories`,
        });
      }
    }
  }
  for (const q of aged) {
    if (q.ageFrom !== undefined && q.ageUntil !== undefined && q.ageUntil <= q.ageFrom) {
      diagnostics.push({
        severity: 'error', path: `quantities.${q.key}.ageUntil`, code: 'bad-age-band',
        message: `"${q.label}" ends at ${q.ageUntil} and starts at ${q.ageFrom}`,
      });
    }
  }
  /*
    THE THREE WAYS A PORTED CARD LIES ABOUT ITS GUESTS.

    All three were live on a production campsite at once, and between them they
    overcharged every quote it produced by two guests. None of them is a broken
    programme — each prices perfectly and answers the wrong question — so none
    of them could be found by comparing totals against the legacy engine, which
    is what the migration checked. They are found by asking whether the card
    still says what the import left behind.
  */

  // 1. A NAME NOBODY GAVE IT. The QPC format has no names for its price slots
  //    — a rate is a base and a numbered amount per slot — so the lift writes
  //    "Quantity 0 (rename me)" and waits. Until somebody does, nothing on the
  //    card records that slot 0 was guests and slot 1 was the dog.
  for (const q of quantities) {
    if (!/\(rename me\)\s*$/i.test(q.label)) continue;
    diagnostics.push({
      severity: 'warning', path: `quantities.${q.key}.label`, code: 'unnamed-quantity',
      message: `"${q.label}" still carries the name the import gave it, so nothing `
        + 'here records what this slot actually counts',
    });
  }

  // 2a. TWO CATEGORIES CLAIMING ONE ROLE THROUGH `alsoCounts`. Unlike two
  //     categories sharing a `channel` — which a park may mean, and which is a
  //     warning below — this one cannot be meant: `alsoCounts` exists to say
  //     "bill this role HERE", and two answers to that is a party whose price
  //     depends on which category was listed first.
  const claimed = new Map<ChannelRole, string>();
  for (const q of quantities) {
    for (const role of (q.alsoCounts ?? [])) {
      if (role === channelRoleOf(q)) continue;
      const first = claimed.get(role);
      if (first !== undefined) {
        diagnostics.push({
          severity: 'error', path: `quantities.${q.key}.alsoCounts`, code: 'role-billed-twice',
          message: `"${role}" is billed by both ${first} and ${q.key}; a party in that `
            + 'role would be priced by whichever came first',
        });
      } else {
        claimed.set(role, q.key);
      }
    }
  }
  for (const q of quantities) {
    const own = channelRoleOf(q);
    const other = claimed.get(own);
    if (other !== undefined && other !== q.key) {
      diagnostics.push({
        severity: 'error', path: `quantities.${other}.alsoCounts`, code: 'role-billed-twice',
        message: `"${own}" is ${q.key}'s own role and ${other} also bills it`,
      });
    }
  }

  // 2. A CATEGORY NO PARTY CAN REACH. A guest picker and every channel speak
  //    roles, not the park's keys, so where two categories claim one role only
  //    the first can ever be billed. Sometimes deliberate — a park splitting
  //    `volwassene` and `senior` — which is why this is a warning. On a ported
  //    card it usually means a second slot never got its role, and the campsite
  //    above is exactly that: its dog was declared an adult and priced as one.
  const byRole = new Map<ChannelRole, string[]>();
  for (const q of quantities) {
    const role = channelRoleOf(q);
    byRole.set(role, [...(byRole.get(role) ?? []), q.key]);
  }
  for (const [role, keys] of byRole) {
    if (keys.length < 2) continue;
    diagnostics.push({
      severity: 'warning', path: `quantities.${keys[1]}`, code: 'unreachable-quantity',
      message: `${keys.join(', ')} all count as "${role}", so a party from a website `
        + `or a channel can only ever reach ${keys[0]}`,
    });
  }

  // 3. AN INCLUSION THAT DOES NOT PRICE. `occupancy.included` is what a channel
  //    is told the base covers; `includedInBase` is what the ENGINE takes off
  //    the bill. They are two spellings of one fact on a card with one guest
  //    category, and a card that has only the first charges from the first
  //    guest while every screen says two are included.
  //    Only where a guest is CHARGED for, though. A card whose rates carry no
  //    per-guest amount bills the same party at the same price however the
  //    inclusion is written, and warning about it would be crying wolf on the
  //    rental units, which sell a price full stop.
  const chargedPerUnit = new Set<string>();
  for (const rate of p.rates ?? []) {
    for (const key of Object.keys(rate.price?.perUnit ?? {})) chargedPerUnit.add(key);
  }
  const people = quantities.filter((q) => countsAsPerson(q) && chargedPerUnit.has(q.key));
  const included = p.occupancy?.included ?? 0;
  const priced = people.reduce((n, q) => n + (q.includedInBase ?? 0), 0);
  if (included > 0 && people.length > 0 && priced === 0) {
    diagnostics.push({
      severity: 'warning', path: 'occupancy.included', code: 'inclusion-not-priced',
      message: `the base is said to cover ${included} `
        + `${included === 1 ? 'person' : 'people'}, but no guest category is priced `
        + 'that way, so the first of them is charged for',
    });
  }

  // Every channel needs somebody to bill. A party of nothing but infants is
  // not a booking any OTA can express, so a programme that maps no category to
  // `adult` cannot be distributed even if it prices perfectly in-house.
  if (quantities.length > 0 && !quantities.some((q) => channelRoleOf(q) === 'adult')) {
    diagnostics.push({
      severity: 'warning', path: 'quantities', code: 'no-adult-category',
      message: 'no guest category maps to a channel adult, so a booking for this '
        + 'accommodation cannot be expressed in the terms every OTA counts in',
    });
  }

  const occupancy = p.occupancy ? { ...p.occupancy } : undefined;
  if (occupancy) {
    // The operator maintains ONE list. Which categories count as people is a
    // property of the categories themselves, so `counts` is derived from them
    // unless it was set explicitly — two lists that must agree is two lists
    // that eventually will not.
    if (!occupancy.counts && quantities.length > 0) {
      const people = quantities.filter(countsAsPerson).map((q) => q.key)
        .filter((k) => k !== occupancy.pets?.quantity);
      if (people.length > 0) occupancy.counts = people;
    }
    if (occupancy.max < occupancy.included) {
      diagnostics.push({
        severity: 'error', path: 'occupancy.max', code: 'occupancy-inverted',
        message: `the limit (${occupancy.max}) is below the number included in the price `
          + `(${occupancy.included}), so no booking could ever use the whole base price`,
      });
    }
    if (occupancy.included < 0 || occupancy.max < 1) {
      diagnostics.push({
        severity: 'error', path: 'occupancy', code: 'occupancy-impossible',
        message: 'an accommodation must take at least one person',
      });
    }
  }

  const known = new Set((p.quantities ?? []).map((q) => q.key));
  const fees = (p.fees ?? []).map((f, i) => {
    const path = `fees[${i}]`;
    if (!(f.amount >= 0)) {
      diagnostics.push({
        severity: 'error', path: `${path}.amount`, code: 'bad-fee-amount',
        message: `${f.label}: an amount must be zero or more`,
      });
    }
    if (f.quantity && !known.has(f.quantity)) {
      diagnostics.push({
        severity: 'error', path: `${path}.quantity`, code: 'unknown-quantity',
        message: `${f.label} counts "${f.quantity}", which is not declared in quantities`,
      });
    }
    if (f.beyondIncluded && !p.occupancy) {
      diagnostics.push({
        severity: 'warning', path: `${path}.beyondIncluded`, code: 'no-occupancy',
        message: `${f.label} charges only the people beyond those included, but this `
          + 'accommodation does not say how many are included, so everyone is charged',
      });
    }
    if ((f.basis === 'perNight' || f.basis === 'perPersonPerNight')
      && f.quantity && f.quantity === p.occupancy?.pets?.quantity
      && p.occupancy.pets.max === 0) {
      diagnostics.push({
        severity: 'warning', path: `${path}.quantity`, code: 'fee-for-nothing',
        message: `${f.label} charges for ${f.quantity}, but none are allowed here`,
      });
    }
    return {
      id: f.id,
      label: f.label,
      basis: f.basis,
      amount: f.amount,
      quantity: f.quantity,
      beyondIncluded: f.beyondIncluded,
      optional: f.optional,
      kind: f.kind,
      active: f.active ? range(f.active, `${path}.active`, diagnostics) : undefined,
    };
  });

  return {
    set: {
      pieces,
      modifiers,
      rounding: p.rounding,
      occupancy,
      guests: Object.keys(guests).length ? guests : undefined,
      fees: fees.length ? fees : undefined,
    },
    diagnostics,
    ok: !diagnostics.some((d) => d.severity === 'error'),
  };
}

export interface NightlyOverlap {
  a: string;
  b: string;
  from: IsoDate;
  until: IsoDate;
}

/**
 * Nightly rates covering the same night.
 *
 * NOT an error. Layering broad over precise is how an operator actually works:
 * low season across 1 April to 1 October, then the general summer price, then
 * the middle-region school holiday, then bouwvak. Each layer is narrower than
 * the one under it, and usually — though not always — dearer.
 *
 * The engine cannot express that on its own. It takes the cheapest legal
 * tiling, by construction, and that is correct behaviour that must not change:
 * it is what makes the whole search work and what the legacy calculator has
 * always done. Left alone, though, it means the broad cheap layer wins and
 * every precise layer above it silently does nothing.
 *
 * So the layers are RESOLVED before the engine sees them — see
 * `flattenNightly`. This function exists to report what overlapped, for a UI
 * that wants to show the operator which layer won where.
 */
export function nightlyOverlaps(rates: readonly Rate[]): NightlyOverlap[] {
  const nightly = rates.filter((r) => r.role === 'nightly')
    .slice()
    .sort((x, y) => x.active.from.localeCompare(y.active.from));
  const out: NightlyOverlap[] = [];
  for (let i = 0; i < nightly.length; i++) {
    for (let j = i + 1; j < nightly.length; j++) {
      const a = nightly[i];
      const b = nightly[j];
      if (b.active.from >= a.active.until) break; // sorted: nothing later overlaps
      out.push({
        a: a.id,
        b: b.id,
        from: b.active.from,
        until: a.active.until < b.active.until ? a.active.until : b.active.until,
      });
    }
  }
  return out;
}

const dayNumber = (iso: IsoDate) => Math.round(Date.parse(`${iso}T00:00:00Z`) / MS_PER_DAY);
const isoFrom = (d: number) => new Date(d * MS_PER_DAY).toISOString().slice(0, 10);

/**
 * Resolve layered nightly bands into non-overlapping ones.
 *
 * THE MOST PRECISE WINDOW WINS — whichever direction the price moves. Bouwvak
 * inside the summer is usually dearer; the last week of the summer holiday is
 * sometimes cheaper. Both are the operator saying something specific about
 * those nights, and specificity is the intent, not price.
 *
 * The alternative, letting the engine's cheapest-tiling rule decide, gets the
 * dear case backwards and silently: the broad cheap layer wins and the precise
 * one never applies, with nothing on screen to say so.
 *
 * Equal spans are resolved by declaration order, last wins, so an operator can
 * always override by adding a layer.
 *
 * Applies ONLY to `role: 'nightly'`. Overlapping ARRANGEMENTS are a different
 * thing entirely: there, competing options that the search picks between is
 * exactly the model, and flattening them would destroy it.
 *
 * And only to bands that can price EVERY night of the window they claim. The
 * specificity being resolved here is a DATE WINDOW; `arriveOn`/`departOn` are a
 * different axis — per placement, so on a one-night rate they mean "only these
 * weekdays are priced here", which is precisely what `arriveOn`'s own
 * documentation says. Treating such a band as a covering layer deleted the band
 * underneath it: a general season at 30 plus a Saturday band at 5 over the same
 * dates left ONLY the Saturday band standing, and an eight-night stay came back
 * unavailable — with the answer flipping to a flat 30 a night, the Saturday
 * price never applying, if the two were declared the other way round.
 *
 * `stayArriveOn` deliberately stays IN. That one is whole-stay eligibility, so
 * a band that carries it really does govern every night of its window; a stay
 * arriving on the wrong day being refused is closed-to-arrival working.
 */
function restrictsPlacementWeekdays(r: Rate): boolean {
  return (r.arriveOn?.length ?? 0) > 0 || (r.departOn?.length ?? 0) > 0;
}

/** Monday is 0, matching `WEEKDAY_ORDER`. Day 0 is a Thursday. */
function weekdayIndexOf(day: number): number {
  return (((day + 3) % 7) + 7) % 7;
}

/**
 * A weekday rule that excludes no night of its own window.
 *
 * `arriveOn: ['friday']` on a rate covering exactly one Friday says nothing the
 * window did not already say — and it is not free, because a rate that
 * restricts placement weekdays is kept out of the layering, so it never cuts
 * the band beneath it and loses to it on price.
 *
 * That is how the calendar used to write every override: it added the weekdays
 * of the selection whenever the selection did not span all seven, so a single
 * night got a rule naming its own weekday. The writer no longer does that, but
 * cards already carry the rates it wrote, and an operator halfway through a
 * season should not have to retype them for a defect that was never theirs.
 *
 * So the test is what the rule EXCLUDES, not whether one is present.
 */
function weekdayRuleIsRedundant(r: Rate): boolean {
  if ((r.departOn?.length ?? 0) > 0) return false;
  const allowed = r.arriveOn;
  if (!allowed || allowed.length === 0) return true;
  const from = dayNumber(r.active.from);
  const until = dayNumber(r.active.until);
  // A window longer than a week contains every weekday, so a rule over it
  // always excludes something.
  if (until - from > 7) return false;
  for (let day = from; day < until; day += 1) {
    if (!allowed.includes(WEEKDAY_ORDER[weekdayIndexOf(day)]!)) return false;
  }
  return true;
}

export function flattenNightly(rates: readonly Rate[]): Rate[] {
  const stated = (r: Rate) => r.role === 'nightly' && weekdayRuleIsRedundant(r);

  /**
   * A day band this card does not call `nightly`, but which an override sits on.
   *
   * A card lifted from a legacy season carries no roles at all, so nothing here
   * saw its day bands — and an operator typing a price into one night of such a
   * card got a rate that layered on top of a band it never cut. The two then
   * competed, the cheapest tiling won, and the edit did nothing unless it
   * happened to be a REDUCTION. Proven on the park's own 2027 draft: two nights
   * set to €80 priced at €77.
   *
   * So a plain day band that an override overlaps is drawn into the layering,
   * where the narrower window wins and the override does what it says.
   *
   * Scoped to overlap ON PURPOSE. A card with no override is untouched, which
   * is every published card in this park — none of them uses the role — so this
   * cannot move a price a guest has been quoted.
   */
  const overrides = rates.filter(stated).map((r) => ({
    from: dayNumber(r.active.from), until: dayNumber(r.active.until),
  }));
  const overlapped = (r: Rate) => {
    if (r.nights !== 1 || r.role || restrictsPlacementWeekdays(r)) return false;
    const a = dayNumber(r.active.from);
    const b = dayNumber(r.active.until);
    return overrides.some((o) => a < o.until && b > o.from);
  };

  const inLayer = (r: Rate) => stated(r) || overlapped(r);
  const layers = rates.filter(inLayer);
  if (layers.length < 2) return [...rates];
  const others = rates.filter((r) => !inLayer(r));

  const cuts = new Set<number>();
  for (const r of layers) {
    cuts.add(dayNumber(r.active.from));
    cuts.add(dayNumber(r.active.until));
  }
  const points = [...cuts].sort((a, b) => a - b);

  const flattened: Rate[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i];
    const until = points[i + 1];
    if (until <= from) continue;

    let winner: Rate | null = null;
    let bestSpan = Infinity;
    for (const r of layers) {
      const a = dayNumber(r.active.from);
      const b = dayNumber(r.active.until);
      if (a > from || b < until) continue; // does not cover this segment
      const span = b - a;
      // `<=` so a later layer of equal width overrides an earlier one.
      if (span <= bestSpan) { winner = r; bestSpan = span; }
    }
    if (!winner) continue; // a gap between layers stays a gap

    const fromIso = isoFrom(from);
    const untilIso = isoFrom(until);
    // Unchanged bands keep their identity, so a diff does not report churn
    // just because a neighbouring layer moved.
    if (winner.active.from === fromIso && winner.active.until === untilIso) {
      flattened.push(winner);
      continue;
    }
    flattened.push({
      ...winner,
      id: `${winner.id}__${fromIso}`,
      active: { from: fromIso, until: untilIso },
    });
  }

  return [...others, ...flattened];
}

/** Diagnostics only, for a UI that validates as you type. */
export function validateProgramme(p: Programme): Diagnostic[] {
  return compileProgramme(p).diagnostics;
}
